import React, { useMemo } from 'react'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import { useQuery, gql } from '@apollo/client'
import CmdKGroup from '../CmdKGroup'
import CommandItem from '../CmdKItem'
import { useOrgName } from 'src/hooks/useOrgName'
import { navigate, routes } from '@cedarjs/router'
import { useDebounce } from 'usehooks-ts'

const INVENTORY_SEARCH_QUERY = gql`
  query InventorySearch($search: String!, $filterLogic: FilterLogic = OR) {
    inventoryItems(
      filters: { partNumber: $search, serialNumber: $search, name: $search }
      filterLogic: $filterLogic
    ) {
      id
      serialNumber
      product {
        id
        partNumber
        name
        type
      }
    }
  }
`

interface CommandMenuGroup {
  heading: string
  items: {
    label: string
    icon?: React.ReactNode
    subtext?: string
    disabled?: boolean
    onClick?: () => void
    children?: {
      label: string
      icon?: React.ReactNode
      subtext?: string
      onClick?: () => void
    }[]
  }[]
}

interface InventoryModuleProps {
  search: string
  onNavigate: (groups: CommandMenuGroup[]) => void
  onBack: () => void
  pages: CommandMenuGroup[][]
  onClose: () => void
}

const InventoryModule: React.FC<InventoryModuleProps> = ({
  onNavigate,
  pages,
  search,
  onClose,
}) => {
  const orgName = useOrgName()
  const debouncedSearch = useDebounce(search, 300)
  const shouldSearch = debouncedSearch && debouncedSearch.length >= 2

  const { data, loading } = useQuery(INVENTORY_SEARCH_QUERY, {
    variables: {
      search: debouncedSearch,
    },
    skip: !shouldSearch,
  })

  const inventoryItems = useMemo(() => {
    if (!data?.inventoryItems) return []

    return data.inventoryItems.map((item) => ({
      label: `${item.product.partNumber} - ${item.serialNumber}`,
      startIcon: <Inventory2Outlined />,
      onSelect: () => {
        onClose()
        navigate(routes.inventoryItemDetail({ orgName, id: item.id }))
      },
    }))
  }, [data?.inventoryItems, orgName, onClose])

  // Don't show anything if we don't have a search term
  // if (!shouldSearch) {
  //   return null
  // }

  if (loading) {
    return (
      <CmdKGroup heading="Inventory">
        <CommandItem disabled>Searching...</CommandItem>
      </CmdKGroup>
    )
  }

  if (!inventoryItems.length) {
    return null
  }

  return (
    <CmdKGroup heading="Inventory">
      {inventoryItems.map((item, index) => (
        <CommandItem
          key={index}
          onSelect={item.onSelect}
          startIcon={item.startIcon}
          keywords={['always show']}
          subtext={'Inventory Item'}
        >
          {item.label}
        </CommandItem>
      ))}
    </CmdKGroup>
  )
}

export default InventoryModule
